import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import Select from "react-select";
import { Box, Button, Stack, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import Helpers from "../../Config/Helpers";
import { useVinDecoder } from "../../hooks/useVinDecoder";
import ReusableFormDialog from "../FormModal/ReusableFormDialog";
import {
  buildCustomerPayload,
  createCustomerInitialValues,
  customerValidationSchema,
  getCustomerFormFields,
  prepareCustomerFormValues,
} from "../Customers/customerFormConfig";
import {
  buildVehicleCustomerOptions,
  buildVehiclePayload,
  createVehicleInitialValues,
  getVehicleFormFields,
  mapVinDecodedVehicle,
  prepareVehicleFormValues,
} from "../Vehicles/vehicleFormConfig";
import { vehicleValidationSchema } from "../FormModal/validationSchemas";

const formatPhone = (value) => {
  const digits = String(value || "")
    .replace(/\D/g, "")
    .slice(0, 10);
  if (digits.length <= 3) return digits ? `(${digits}` : "";
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

const formatVehicleLabel = (vehicle = {}) => {
  const baseLabel = [vehicle?.year, vehicle?.make || vehicle?.name, vehicle?.model]
    .filter(Boolean)
    .join(" ");
  const vin = String(vehicle?.vin_number || "")
    .replace(/\s/g, "")
    .toUpperCase();
  const vinSuffix = vin ? vin.slice(-8) : "";
  return [baseLabel, vinSuffix].filter(Boolean).join(" · ") || "Vehicle";
};

const buildCustomerOption = (customer) =>
  customer
    ? {
        label: `${customer.name || ""}${
          customer.phone ? ` ${formatPhone(customer.phone)}` : ""
        }`.trim(),
        value: customer,
      }
    : null;

const buildVehicleOption = (vehicle) =>
  vehicle
    ? {
        label: formatVehicleLabel(vehicle),
        value: vehicle,
      }
    : null;

const buildVehicleOptions = (vehicles = []) =>
  (Array.isArray(vehicles) ? vehicles : []).map(buildVehicleOption).filter(Boolean);

const getCustomerId = (customer = null) =>
  typeof customer === "object" ? customer?._id || "" : String(customer || "");

const getVehicleCustomer = (vehicle = null) =>
  vehicle?.customer && typeof vehicle.customer === "object"
    ? vehicle.customer
    : null;

const selectStyles = {
  menu: (base) => ({ ...base, zIndex: 1700 }),
  menuPortal: (base) => ({ ...base, zIndex: 1700 }),
};

function AppointmentCustomerVehicleSelect({
  customer,
  vehicle,
  onCustomerChange,
  onVehicleChange,
  onUnauthenticated,
}) {
  const [customers, setCustomers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [vehicleOptions, setVehicleOptions] = useState([]);
  const [customerSearchInput, setCustomerSearchInput] = useState("");
  const [vehicleSearchLoading, setVehicleSearchLoading] = useState(false);
  const [vehicleSearchInput, setVehicleSearchInput] = useState("");
  const [customerDialogOpen, setCustomerDialogOpen] = useState(false);
  const [vehicleDialogOpen, setVehicleDialogOpen] = useState(false);
  const [customerSaving, setCustomerSaving] = useState(false);
  const [vehicleSaving, setVehicleSaving] = useState(false);
  const [customerDraft, setCustomerDraft] = useState(() =>
    createCustomerInitialValues(Helpers.authParentId),
  );
  const [vehicleDraft, setVehicleDraft] = useState(() =>
    createVehicleInitialValues(Helpers.authParentId),
  );
  const [onlyFillEmptyVehicleVin, setOnlyFillEmptyVehicleVin] = useState(true);
  const vehicleSearchTimeoutRef = useRef(null);
  const vehicleSearchRequestIdRef = useRef(0);
  const vehicleFormikValuesRef = useRef({});
  const vehicleFormikSetFieldValueRef = useRef(null);
  const lastDecodedVinRef = useRef("");
  const lastDecodedVehicleRef = useRef(null);

  const availableCustomers = useMemo(() => {
    const list = Array.isArray(customers) ? customers : [];
    const selectedCustomerId = String(customer?._id || "");
    if (
      !selectedCustomerId ||
      list.some((item) => String(item?._id || "") === selectedCustomerId)
    ) {
      return list;
    }
    return [customer, ...list];
  }, [customer, customers]);
  const customerOptions = useMemo(
    () => availableCustomers.map(buildCustomerOption).filter(Boolean),
    [availableCustomers],
  );
  const customerFields = useMemo(() => getCustomerFormFields(), []);
  const vehicleCustomerOptions = useMemo(
    () => buildVehicleCustomerOptions(availableCustomers),
    [availableCustomers],
  );
  const selectedCustomerOption = buildCustomerOption(customer);
  const selectedVehicleOption = buildVehicleOption(vehicle);
  const menuPortalTarget =
    typeof document !== "undefined" ? document.body : null;

  const handleRequestError = useCallback(
    (error) => {
      if (onUnauthenticated) {
        onUnauthenticated(error);
        return;
      }
      Helpers.error_response(error);
    },
    [onUnauthenticated],
  );

  const mergeCustomer = useCallback((nextCustomer) => {
    if (!nextCustomer?._id) return;
    setCustomers((current) => {
      const list = Array.isArray(current) ? current : [];
      const exists = list.some(
        (item) => String(item?._id || "") === String(nextCustomer._id),
      );
      if (exists) {
        return list.map((item) =>
          String(item?._id || "") === String(nextCustomer._id)
            ? nextCustomer
            : item,
        );
      }
      return [nextCustomer, ...list];
    });
  }, []);

  const mergeVehicle = useCallback((nextVehicle) => {
    if (!nextVehicle?._id) return;
    setVehicles((current) => {
      const list = Array.isArray(current) ? current : [];
      const exists = list.some(
        (item) => String(item?._id || "") === String(nextVehicle._id),
      );
      if (exists) {
        return list.map((item) =>
          String(item?._id || "") === String(nextVehicle._id)
            ? nextVehicle
            : item,
        );
      }
      return [nextVehicle, ...list];
    });
    setVehicleOptions((current) => {
      const option = buildVehicleOption(nextVehicle);
      const list = Array.isArray(current) ? current : [];
      const exists = list.some(
        (item) =>
          String(item?.value?._id || "") === String(nextVehicle._id),
      );
      if (exists) {
        return list.map((item) =>
          String(item?.value?._id || "") === String(nextVehicle._id)
            ? option
            : item,
        );
      }
      return [option, ...list].filter(Boolean);
    });
  }, []);

  const getCustomerVehicles = useCallback(
    (customerId) => {
      if (!customerId) {
        setVehicles([]);
        setVehicleOptions(vehicle ? buildVehicleOptions([vehicle]) : []);
        return Promise.resolve([]);
      }

      return axios
        .get(`${Helpers.baseUrl}/vehicles/customer/${customerId}`, Helpers.headers)
        .then((response) => {
          const data = Array.isArray(response.data) ? response.data.reverse() : [];
          setVehicles(data);
          setVehicleOptions(buildVehicleOptions(data));
          return data;
        })
        .catch((error) => {
          handleRequestError(error);
          return [];
        });
    },
    [handleRequestError, vehicle],
  );

  const restoreVehicleOptions = useCallback(
    (customerId = "") => {
      if (customerId) {
        const mergedVehicles = [...(Array.isArray(vehicles) ? vehicles : [])];
        const selectedVehicleId = String(vehicle?._id || vehicle?.id || "");
        if (
          vehicle &&
          selectedVehicleId &&
          !mergedVehicles.some(
            (item) => String(item?._id || item?.id || "") === selectedVehicleId,
          )
        ) {
          mergedVehicles.push(vehicle);
        }
        setVehicleOptions(buildVehicleOptions(mergedVehicles));
        return;
      }

      setVehicleOptions(vehicle ? buildVehicleOptions([vehicle]) : []);
    },
    [vehicle, vehicles],
  );

  useEffect(() => {
    axios
      .get(`${Helpers.baseUrl}/customers/all/${Helpers.authParentId}`, Helpers.headers)
      .then((response) => {
        setCustomers(Array.isArray(response.data) ? response.data.reverse() : []);
      })
      .catch(handleRequestError);
  }, [handleRequestError]);

  useEffect(() => {
    const customerId = getCustomerId(customer);
    if (customerId) {
      getCustomerVehicles(customerId);
    } else {
      restoreVehicleOptions("");
    }
  }, [customer, getCustomerVehicles, restoreVehicleOptions]);

  useEffect(
    () => () => {
      if (vehicleSearchTimeoutRef.current) {
        clearTimeout(vehicleSearchTimeoutRef.current);
      }
    },
    [],
  );

  const filterCustomerOption = (option, inputValue = "") => {
    const term = String(inputValue || "").toLowerCase();
    if (!term) return true;

    const optionCustomer = option?.data?.value || {};
    const phoneDigits = String(optionCustomer.phone || "").replace(/\D/g, "");
    const termDigits = term.replace(/\D/g, "");

    return (
      String(optionCustomer.name || "").toLowerCase().includes(term) ||
      String(optionCustomer.email || "").toLowerCase().includes(term) ||
      String(optionCustomer.phone || "").toLowerCase().includes(term) ||
      (termDigits ? phoneDigits.includes(termDigits) : false)
    );
  };

  const handleSelectCustomer = (selectedOption) => {
    const nextCustomer = selectedOption?.value || null;
    setVehicleSearchInput("");
    onCustomerChange(nextCustomer);
    onVehicleChange(null);

    if (nextCustomer?._id) {
      getCustomerVehicles(nextCustomer._id);
    } else {
      setVehicleOptions([]);
    }
  };

  const handleCustomerInputChange = (inputValue, actionMeta) => {
    if (actionMeta?.action === "input-change") {
      setCustomerSearchInput(inputValue);
    }
    return inputValue;
  };

  const handleSelectVehicle = (selectedOption) => {
    const nextVehicle = selectedOption?.value || null;
    setVehicleSearchInput("");

    const vehicleCustomer = getVehicleCustomer(nextVehicle);
    const vehicleCustomerId = String(vehicleCustomer?._id || "");
    const selectedCustomerId = String(customer?._id || "");
    if (vehicleCustomerId && vehicleCustomerId !== selectedCustomerId) {
      onCustomerChange(vehicleCustomer);
    }
    onVehicleChange(nextVehicle);

    if (vehicleCustomer?._id) {
      getCustomerVehicles(vehicleCustomer._id);
    }
  };

  const searchVehicles = (inputValue = "") => {
    const query = String(inputValue || "").trim();
    setVehicleSearchInput(query);

    if (vehicleSearchTimeoutRef.current) {
      clearTimeout(vehicleSearchTimeoutRef.current);
    }

    if (!query) {
      vehicleSearchRequestIdRef.current += 1;
      setVehicleSearchLoading(false);
      restoreVehicleOptions(getCustomerId(customer));
      return;
    }

    vehicleSearchTimeoutRef.current = setTimeout(() => {
      const requestId = ++vehicleSearchRequestIdRef.current;
      setVehicleSearchLoading(true);
      axios
        .get(`${Helpers.baseUrl}/vehicles/search`, {
          ...Helpers.headers,
          params: { q: query },
        })
        .then((response) => {
          if (requestId !== vehicleSearchRequestIdRef.current) return;
          setVehicleOptions(buildVehicleOptions(response.data));
        })
        .catch(handleRequestError)
        .finally(() => {
          if (requestId !== vehicleSearchRequestIdRef.current) return;
          setVehicleSearchLoading(false);
        });
    }, 250);
  };

  const handleVehicleInputChange = (inputValue, actionMeta) => {
    if (actionMeta?.action === "input-change") {
      searchVehicles(inputValue);
    }
    return inputValue;
  };

  const getPhoneFromCustomerSearch = () => {
    const digits = String(customerSearchInput || "")
      .replace(/\D/g, "")
      .slice(0, 10);
    return digits.length >= 7 ? formatPhone(digits) : "";
  };

  const closeCustomerDialog = () => {
    setCustomerDialogOpen(false);
    setCustomerDraft(createCustomerInitialValues(Helpers.authParentId));
  };

  const openAddCustomerDialog = () => {
    setCustomerDraft({
      ...createCustomerInitialValues(Helpers.authParentId),
      phone: getPhoneFromCustomerSearch(),
    });
    setCustomerDialogOpen(true);
  };

  const closeVehicleDialog = () => {
    setVehicleDialogOpen(false);
    setVehicleDraft(createVehicleInitialValues(Helpers.authParentId));
    setOnlyFillEmptyVehicleVin(true);
    lastDecodedVinRef.current = "";
    lastDecodedVehicleRef.current = null;
  };

  const openAddVehicleDialog = () => {
    setVehicleDraft(
      prepareVehicleFormValues(
        { customer: customer?._id || "" },
        Helpers.authParentId,
      ),
    );
    setOnlyFillEmptyVehicleVin(true);
    lastDecodedVinRef.current = "";
    lastDecodedVehicleRef.current = null;
    setVehicleDialogOpen(true);
  };

  const saveCustomer = (values) => {
    const payload = buildCustomerPayload(values, Helpers.authParentId);
    setCustomerSaving(true);
    axios
      .post(`${Helpers.baseUrl}/customers/add`, payload, Helpers.headers)
      .then((response) => {
        const savedCustomer = response.data;
        mergeCustomer(savedCustomer);
        onCustomerChange(savedCustomer);
        onVehicleChange(null);
        setCustomerSearchInput("");
        getCustomerVehicles(savedCustomer?._id);
        Helpers.toast("success", "Customer saved successfully");
        closeCustomerDialog();
      })
      .catch((error) => {
        Helpers.error_response(error);
        handleRequestError(error);
      })
      .finally(() => setCustomerSaving(false));
  };

  const saveVehicle = (values) => {
    const payload = buildVehiclePayload(
      values,
      availableCustomers,
      Helpers.authParentId,
    );

    setVehicleSaving(true);
    axios
      .post(`${Helpers.baseUrl}/vehicles/add`, payload, Helpers.headers)
      .then((response) => {
        const savedVehicle = response.data;
        const vehicleCustomer = getVehicleCustomer(savedVehicle);
        const vehicleCustomerId = String(vehicleCustomer?._id || "");
        const selectedCustomerId = String(customer?._id || "");

        mergeVehicle(savedVehicle);
        if (vehicleCustomerId && vehicleCustomerId !== selectedCustomerId) {
          mergeCustomer(vehicleCustomer);
          onCustomerChange(vehicleCustomer);
        }
        onVehicleChange(savedVehicle);
        if (vehicleCustomerId) {
          getCustomerVehicles(vehicleCustomerId);
        }
        Helpers.toast("success", "Vehicle saved successfully");
        closeVehicleDialog();
      })
      .catch((error) => {
        Helpers.error_response(error);
        handleRequestError(error);
      })
      .finally(() => setVehicleSaving(false));
  };

  const clearDecodedVehicleFieldsIfNeeded = useCallback((nextVin) => {
    const normalizedVin = String(nextVin || "").trim().toUpperCase();
    const lastDecodedVin = String(lastDecodedVinRef.current || "")
      .trim()
      .toUpperCase();

    if (!lastDecodedVin || normalizedVin === lastDecodedVin) {
      return;
    }

    const setFieldValue = vehicleFormikSetFieldValueRef.current;
    const values = vehicleFormikValuesRef.current || {};
    const lastDecodedVehicle = lastDecodedVehicleRef.current;

    if (!setFieldValue || !lastDecodedVehicle) {
      lastDecodedVinRef.current = "";
      lastDecodedVehicleRef.current = null;
      return;
    }

    ["year", "make", "model", "engine_size", "engine_cylinders", "drivetrain"].forEach(
      (field) => {
        if ((values?.[field] || "") === (lastDecodedVehicle?.[field] || "")) {
          setFieldValue(field, "");
        }
      },
    );

    lastDecodedVinRef.current = "";
    lastDecodedVehicleRef.current = null;
  }, []);

  const handleVinDecoded = useCallback((mapped, onlyFillEmpty, sourceVin = "") => {
    const setFieldValue = vehicleFormikSetFieldValueRef.current;
    const values = vehicleFormikValuesRef.current || {};
    if (!setFieldValue) return;

    const normalizedDecodedVehicle = mapVinDecodedVehicle(mapped, {}, false);
    const nextValues = mapVinDecodedVehicle(mapped, values, onlyFillEmpty);
    setFieldValue("year", nextValues.year);
    setFieldValue("make", nextValues.make);
    setFieldValue("model", nextValues.model);
    setFieldValue("engine_size", nextValues.engine_size);
    setFieldValue("engine_cylinders", nextValues.engine_cylinders);
    setFieldValue("drivetrain", nextValues.drivetrain);
    lastDecodedVinRef.current = String(sourceVin || "").trim().toUpperCase();
    lastDecodedVehicleRef.current = normalizedDecodedVehicle;
  }, []);

  const { vinLoading, vinError, onlyFillEmptyRef, onVinChange, onVinBlur } =
    useVinDecoder(handleVinDecoded, {
      baseUrl: Helpers.baseUrl,
      axiosConfig: Helpers.headers,
      endpointPrefix: "vin/",
    });

  const vehicleFields = useMemo(
    () =>
      getVehicleFormFields({
        customerOptions: vehicleCustomerOptions,
        customersLoaded: vehicleCustomerOptions.length > 0,
        onlyFillEmptyVin: onlyFillEmptyVehicleVin,
        setOnlyFillEmptyVin: setOnlyFillEmptyVehicleVin,
        onlyFillEmptyRef,
        onVinInputChange: clearDecodedVehicleFieldsIfNeeded,
        onVinChange,
        onVinBlur,
        vinLoading,
        vinError,
        formikValuesRef: vehicleFormikValuesRef,
        formikSetFieldValueRef: vehicleFormikSetFieldValueRef,
      }),
    [
      vehicleCustomerOptions,
      onlyFillEmptyVehicleVin,
      onlyFillEmptyRef,
      clearDecodedVehicleFieldsIfNeeded,
      onVinChange,
      onVinBlur,
      vinLoading,
      vinError,
    ],
  );

  return (
    <>
      <Stack spacing={2}>
        <Box>
          <Typography sx={{ fontWeight: 600, mb: 0.75 }}>Customer</Typography>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            alignItems={{ xs: "stretch", sm: "center" }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Select
                options={customerOptions}
                isClearable
                value={selectedCustomerOption}
                onChange={handleSelectCustomer}
                onInputChange={handleCustomerInputChange}
                filterOption={filterCustomerOption}
                placeholder="Search customer by name or phone"
                noOptionsMessage={() => "No matching customers"}
                menuPortalTarget={menuPortalTarget}
                menuPosition="fixed"
                styles={selectStyles}
              />
            </Box>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={openAddCustomerDialog}
              sx={{
                minHeight: 38,
                whiteSpace: "nowrap",
                width: { xs: "100%", sm: "auto" },
              }}
            >
              New
            </Button>
          </Stack>
        </Box>

        <Box>
          <Typography sx={{ fontWeight: 600, mb: 0.75 }}>Vehicle</Typography>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            alignItems={{ xs: "stretch", sm: "center" }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Select
                options={vehicleOptions}
                isClearable
                isLoading={vehicleSearchLoading}
                value={selectedVehicleOption}
                onChange={handleSelectVehicle}
                onInputChange={handleVehicleInputChange}
                placeholder="Search year, make, model, or last 8 of VIN"
                noOptionsMessage={() => {
                  if (vehicleSearchLoading) return "Searching vehicles...";
                  if (vehicleSearchInput) return "No matching vehicles";
                  if (customer?._id) return "No vehicles for this customer";
                  return "Start typing to search vehicles";
                }}
                menuPortalTarget={menuPortalTarget}
                menuPosition="fixed"
                styles={selectStyles}
              />
            </Box>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={openAddVehicleDialog}
              sx={{
                minHeight: 38,
                whiteSpace: "nowrap",
                width: { xs: "100%", sm: "auto" },
              }}
            >
              New
            </Button>
          </Stack>
        </Box>
      </Stack>

      <ReusableFormDialog
        open={customerDialogOpen}
        onClose={closeCustomerDialog}
        title="Add New Customer"
        initialValues={prepareCustomerFormValues(customerDraft, Helpers.authParentId)}
        validationSchema={customerValidationSchema}
        fields={customerFields}
        onSubmit={saveCustomer}
        loading={customerSaving}
      />

      <ReusableFormDialog
        open={vehicleDialogOpen}
        onClose={closeVehicleDialog}
        title="Add New Vehicle"
        initialValues={prepareVehicleFormValues(vehicleDraft, Helpers.authParentId)}
        validationSchema={vehicleValidationSchema}
        fields={vehicleFields}
        onSubmit={saveVehicle}
        loading={vehicleSaving}
        />
    </>
  );
}

export default AppointmentCustomerVehicleSelect;
